/**
 * Embedded inside Shopify.js's tab area. Receives storeId + period from parent.
 * Server-side aggregates line items in the period; we paginate the ranking.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Box, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { useToast } from './ToastContainer';
import api from '../utils/api';
import './Shopify.css';

const moneyFmt = (v, code) => {
  if (v == null) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: code || 'USD', maximumFractionDigits: 0,
    }).format(Number(v));
  } catch { return `${code || ''} ${Number(v).toLocaleString()}`; }
};

const ShopifyProducts = ({ storeId, period = '30d', currency = 'SAR' }) => {
  const { showError } = useToast();
  const [sortBy, setSortBy] = useState('revenue');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [storeId, period, sortBy, search]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period, sortBy, page, limit: pageSize });
      if (storeId) params.set('storeId', storeId);
      if (search) params.set('search', search);
      const res = await api.get(`/api/shopify/products/performance?${params}`);
      setItems(res.data.items || []);
      setPagination(res.data.pagination || { total: 0, totalPages: 1 });
    } catch (e) {
      showError('Failed to load products: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }, [period, sortBy, storeId, search, page, pageSize, showError]);

  useEffect(() => { load(); }, [load]);

  // Compute the rank-from-1 offset for the current page so the rank column
  // shows global ranking, not 1..N per page.
  const startRank = (page - 1) * pageSize;

  return (
    <div className="shop-panel">
      <div className="shop-tab-toolbar">
        <select className="shop-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="revenue">Sort: Revenue</option>
          <option value="units">Sort: Units sold</option>
        </select>
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.4)' }} />
          <input
            className="shop-select"
            style={{ paddingLeft: 32, width: '100%' }}
            placeholder="Search title or SKU…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem' }}>
          Aggregated from line items in the selected period.
        </span>
      </div>

      {loading ? (
        <div className="shop-loading" style={{ minHeight: 200 }}><div className="shop-spinner" /></div>
      ) : items.length === 0 ? (
        <div className="shop-empty">
          <Box size={32} style={{ opacity: 0.4 }} />
          <div style={{ marginTop: '0.5rem' }}>
            {search ? `No products matching "${search}".` : 'No sales in this window.'}
          </div>
        </div>
      ) : (
        <>
          <table className="shop-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Product</th>
                <th>SKU</th>
                <th>Units sold</th>
                <th>Revenue</th>
                <th>Orders</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <motion.tr key={it.key} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.15 }}>
                  <td style={{ color: 'rgba(255,255,255,0.4)', fontVariantNumeric: 'tabular-nums' }}>{startRank + i + 1}</td>
                  <td>{it.title || '—'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)' }}>{it.sku || '—'}</td>
                  <td>{it.units}</td>
                  <td style={{ fontWeight: 600, color: '#10b981' }}>{moneyFmt(it.revenue, currency)}</td>
                  <td>{it.orders}</td>
                </motion.tr>
              ))}
            </tbody>
          </table>

          <div className="shop-pagination">
            <div>
              Show <select className="shop-select" style={{ display: 'inline-block', padding: '0.25rem 0.5rem' }} value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select> per page
            </div>
            <div className="shop-pagination-controls">
              <button className="shop-page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={14} /> Prev</button>
              <span className="shop-page-info">Page {page} of {pagination.totalPages || 1}</span>
              <button className="shop-page-btn" disabled={page >= (pagination.totalPages || 1)} onClick={() => setPage(p => p + 1)}>Next <ChevronRight size={14} /></button>
            </div>
            <div className="shop-page-total">{pagination.total.toLocaleString()} products in window</div>
          </div>
        </>
      )}
    </div>
  );
};

export default ShopifyProducts;
